/**
 * Homepage hero entry (BUILD_GAME §12.1) — a few hundred bytes next to the
 * chess bundle. On idle, on a Medium-or-better device without Save-Data, it
 * prefetches the game (shell, stylesheet, engine chunk) into the HTTP cache so
 * "Play" opens fast; it fills today's stats; it counts the funnel.
 *
 * Proposed as its own esbuild entry (`hero`) in the game pass; until then the
 * server renders no script for it and the hero is plain HTML, which works.
 */

import { createApi } from './api.js';
import { formatInt, formatTime } from './format.js';
import { canPrefetch } from './prefetch-policy.js';
import { safeStorage } from './storage.js';
import { visitEvent } from './visit.js';

const hero = document.querySelector('[data-build-hero]');
if (hero) start(hero);

function prefetch(urls) {
  for (const href of urls) {
    const link = document.createElement('link');
    link.rel = 'prefetch';
    link.as = href.endsWith('.css') ? 'style' : 'script';
    link.href = href;
    document.head.append(link);
  }
}

function start(hero) {
  const api = createApi();
  const levelId = hero.dataset.level ?? 'l1';
  const locale = document.documentElement.lang || 'en';
  const idle = globalThis.requestIdleCallback ?? ((callback) => setTimeout(callback, 1200));

  /*
   * Idle AFTER load: a module script runs before `load`, and an idle slot can
   * open while the poster is still the page's pending LCP — prefetching ~200 KB
   * there competes with it (§12.1 "Do not block LCP").
   */
  const afterLoad = (callback) =>
    document.readyState === 'complete'
      ? callback()
      : addEventListener('load', callback, { once: true });
  afterLoad(() =>
    idle(() => {
      if (!canPrefetch()) return;
      try {
        prefetch(JSON.parse(hero.dataset.prefetch ?? '[]'));
      } catch {
        /* nothing to prefetch */
      }
    }),
  );

  api.event('hero_view', levelId);
  const visit = visitEvent(safeStorage());
  if (visit) api.event(visit, levelId);
  hero.querySelector('[data-build-hero-play]')?.addEventListener('click', () => {
    api.event('play_click', levelId);
  });

  const daily = document.querySelector('[data-build-daily]');
  api
    .daily(levelId)
    .then((stats) => {
      const players = daily?.querySelector('[data-daily-players]');
      const median = daily?.querySelector('[data-daily-median]');
      if (players && Number.isFinite(stats?.players)) {
        players.textContent = formatInt(stats.players, locale);
      }
      if (median && Number.isFinite(stats?.medianFinalMs)) {
        median.textContent = formatTime(stats.medianFinalMs, locale);
      }
    })
    .catch(() => {
      /* the dashes stay: nothing is shown that the server did not say */
    });
}
